/**
 * Service for ESCO Occupations API
 * Base URL: https://apicabaza.creago.be/rest/cabaza/occupations
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/occupations';

/**
 * Search occupations via ESCO API
 * @param {string} text - Search term
 * @param {string} language - Language code (fr, en, nl, de, etc.)
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Array of occupation suggestions
 */
export const searchOccupationsEsco = async (text, language = 'fr', limit = 10) => {
    try {
        const params = new URLSearchParams({
            text,
            language,
            limit: limit.toString()
        });

        const response = await fetch(`${API_BASE_URL}/search?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];
        const message = result.message ?? result.MESSAGE;

        if (success) {
            // Normalize data to lowercase keys
            return data.map(item => ({
                uri: item.uri ?? item.URI,
                title: item.title ?? item.TITLE,
                preferredLabel: item.preferredLabel ?? item.PREFERREDLABEL ?? {}
            }));
        }

        console.error('ESCO search error:', message);
        return [];
    } catch (error) {
        console.error('Error searching ESCO occupations:', error);
        return [];
    }
};

/**
 * Get ESCO occupation details by URI
 * @param {string} uri - ESCO URI
 * @returns {Promise<Object>} Occupation details with translations
 */
export const getEscoOccupationDetails = async (uri) => {
    try {
        const params = new URLSearchParams({ uri });

        const response = await fetch(`${API_BASE_URL}/esco-details?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;
        const message = result.message ?? result.MESSAGE;

        if (success && data) {
            // Normalize keys to lowercase
            return {
                uri: data.uri ?? data.URI,
                title: data.title ?? data.TITLE,
                name_fr: data.name_fr ?? data.NAME_FR ?? '',
                name_en: data.name_en ?? data.NAME_EN ?? '',
                name_nl: data.name_nl ?? data.NAME_NL ?? '',
                name_de: data.name_de ?? data.NAME_DE ?? '',
                name_es: data.name_es ?? data.NAME_ES ?? '',
                name_it: data.name_it ?? data.NAME_IT ?? '',
                name_pt: data.name_pt ?? data.NAME_PT ?? '',
                name_el: data.name_el ?? data.NAME_EL ?? '',
                description_fr: data.description_fr ?? data.DESCRIPTION_FR ?? '',
                description_en: data.description_en ?? data.DESCRIPTION_EN ?? '',
                description_nl: data.description_nl ?? data.DESCRIPTION_NL ?? '',
                description_de: data.description_de ?? data.DESCRIPTION_DE ?? '',
                description_es: data.description_es ?? data.DESCRIPTION_ES ?? '',
                description_it: data.description_it ?? data.DESCRIPTION_IT ?? '',
                description_pt: data.description_pt ?? data.DESCRIPTION_PT ?? '',
                description_el: data.description_el ?? data.DESCRIPTION_EL ?? ''
            };
        }

        console.error('ESCO details error:', message);
        return null;
    } catch (error) {
        console.error('Error getting ESCO occupation details:', error);
        return null;
    }
};

/**
 * Save occupation to local database
 * @param {Object} occupationData - Occupation data with translations
 * @returns {Promise<Object>} Saved occupation with ID
 */
export const saveOccupation = async (occupationData) => {
    try {
        const response = await fetch(API_BASE_URL, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(occupationData)
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;
        const message = result.message ?? result.MESSAGE;

        if (success && data) {
            return {
                id: data.id ?? data.ID,
                escoUri: data.escoUri ?? data.ESCOURI,
                existing: data.existing ?? data.EXISTING
            };
        }

        console.error('Save occupation error:', message);
        return null;
    } catch (error) {
        console.error('Error saving occupation:', error);
        return null;
    }
};

/**
 * Get occupation by ID from local database
 * @param {number} id - Occupation ID
 * @returns {Promise<Object>} Occupation object
 */
export const getOccupationById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        if (success && data) {
            // Normalize keys to lowercase
            return {
                id: data.id ?? data.ID,
                escoUri: data.escoUri ?? data.ESCOURI,
                name_fr: data.name_fr ?? data.NAME_FR ?? '',
                name_en: data.name_en ?? data.NAME_EN ?? '',
                name_nl: data.name_nl ?? data.NAME_NL ?? '',
                name_de: data.name_de ?? data.NAME_DE ?? '',
                name_es: data.name_es ?? data.NAME_ES ?? '',
                name_it: data.name_it ?? data.NAME_IT ?? '',
                name_pt: data.name_pt ?? data.NAME_PT ?? '',
                name_el: data.name_el ?? data.NAME_EL ?? '',
                description_fr: data.description_fr ?? data.DESCRIPTION_FR ?? '',
                description_en: data.description_en ?? data.DESCRIPTION_EN ?? '',
                description_nl: data.description_nl ?? data.DESCRIPTION_NL ?? '',
                description_de: data.description_de ?? data.DESCRIPTION_DE ?? '',
                description_es: data.description_es ?? data.DESCRIPTION_ES ?? '',
                description_it: data.description_it ?? data.DESCRIPTION_IT ?? '',
                description_pt: data.description_pt ?? data.DESCRIPTION_PT ?? '',
                description_el: data.description_el ?? data.DESCRIPTION_EL ?? ''
            };
        }

        return null;
    } catch (error) {
        console.error('Error getting occupation:', error);
        return null;
    }
};

/**
 * Search occupations in local database
 * @param {string} keyword - Search term
 * @param {string} lang - Language code
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Array of occupations
 */
export const searchOccupationsLocal = async (keyword, lang = 'fr', limit = 20) => {
    try {
        const params = new URLSearchParams({
            keyword,
            lang,
            limit: limit.toString()
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await response.json();

        // Handle both lowercase and uppercase keys (ColdFusion returns uppercase)
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        if (success) {
            // Normalize data to lowercase keys
            return data.map(item => ({
                id: item.id ?? item.ID,
                escoUri: item.escoUri ?? item.ESCOURI,
                name_fr: item.name_fr ?? item.NAME_FR ?? '',
                name_en: item.name_en ?? item.NAME_EN ?? '',
                name_nl: item.name_nl ?? item.NAME_NL ?? '',
                name_de: item.name_de ?? item.NAME_DE ?? ''
            }));
        }

        return [];
    } catch (error) {
        console.error('Error searching local occupations:', error);
        return [];
    }
};

/**
 * Select and save occupation
 * 1. Check if exists in local DB
 * 2. If not, get details from ESCO API
 * 3. Save to local DB
 * 4. Return occupation ID
 *
 * @param {Object} escoOccupation - Selected occupation from ESCO search
 * @returns {Promise<Object>} Saved occupation with ID
 */
export const selectOccupation = async (escoOccupation) => {
    try {
        // First try to save (will return existing if already exists)
        const occupationData = {
            escoUri: escoOccupation.uri,
            name_fr: escoOccupation.preferredLabel?.fr || escoOccupation.title || '',
            name_en: escoOccupation.preferredLabel?.en || escoOccupation.title || '',
            name_nl: escoOccupation.preferredLabel?.nl || '',
            name_de: escoOccupation.preferredLabel?.de || '',
            name_es: escoOccupation.preferredLabel?.es || '',
            name_it: escoOccupation.preferredLabel?.it || '',
            name_pt: escoOccupation.preferredLabel?.pt || '',
            name_el: escoOccupation.preferredLabel?.el || '',
            description_fr: '',
            description_en: '',
            description_nl: '',
            description_de: '',
            description_es: '',
            description_it: '',
            description_pt: '',
            description_el: ''
        };

        // Always fetch details from ESCO API to get descriptions
        const details = await getEscoOccupationDetails(escoOccupation.uri);
        if (details) {
            occupationData.name_fr = details.name_fr || occupationData.name_fr;
            occupationData.name_en = details.name_en || occupationData.name_en;
            occupationData.name_nl = details.name_nl || occupationData.name_nl;
            occupationData.name_de = details.name_de || occupationData.name_de;
            occupationData.name_es = details.name_es || occupationData.name_es;
            occupationData.name_it = details.name_it || occupationData.name_it;
            occupationData.name_pt = details.name_pt || occupationData.name_pt;
            occupationData.name_el = details.name_el || occupationData.name_el;
            // Add descriptions
            occupationData.description_fr = details.description_fr || '';
            occupationData.description_en = details.description_en || '';
            occupationData.description_nl = details.description_nl || '';
            occupationData.description_de = details.description_de || '';
            occupationData.description_es = details.description_es || '';
            occupationData.description_it = details.description_it || '';
            occupationData.description_pt = details.description_pt || '';
            occupationData.description_el = details.description_el || '';
        }

        const savedOccupation = await saveOccupation(occupationData);
        return savedOccupation;
    } catch (error) {
        console.error('Error selecting occupation:', error);
        return null;
    }
};
